/**
 * Raising a ticket.
 *
 * Two paths, phrased the way an employee thinks about them rather than the way
 * Ivanti models them: "something is broken" (an incident) and "I need
 * something" (a service request from the catalog). The tab labels carry the
 * plain-language wording; the ITIL vocabulary never reaches the screen.
 */

import { CircleAlert, Icon, ShoppingBag } from "@norican/icons";
import { Alert, Button, Tabs, Typography } from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import {
  AppBreadcrumbs,
  ErrorState,
  LoadingState,
  PageContainer,
  SectionCard,
} from "@/components/common";
// Not the access feature's index: it imports this feature's, and these two
// modules import nothing of either.
import { accessKeys } from "@/features/access/api";
import { accessCasePath } from "@/features/access/lib/access";
import {
  starterKeys,
  type StarterStep,
  starterStepFromParams,
  useStarterPrefill,
} from "@/features/starters";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import { useMe } from "@/lib/api";

import type { TicketKind } from "./api/contract";
import { useTicket } from "./api/queries";
import { IncidentForm } from "./components/incident-form";
import { ServiceRequestForm } from "./components/service-request-form";
import { TicketErrorBoundary } from "./components/ticket-error-boundary";
import { TranslationPreferenceProvider } from "./components/translation-preference";

function kindFromParam(param: string | null, offering: string | null = null): TicketKind {
  return param === "SERVICE_REQUEST" || offering !== null ? "SERVICE_REQUEST" : "INCIDENT";
}

export function TicketCreatePage() {
  return (
    <TicketErrorBoundary>
      <TranslationPreferenceProvider>
        <TicketCreateView />
      </TranslationPreferenceProvider>
    </TicketErrorBoundary>
  );
}

function TicketCreateView() {
  const { t } = useT("tickets");
  useDocumentTitle(t("new.title"));
  // `?kind=SERVICE_REQUEST` opens straight on the catalog tab. The incident
  // form links here when somebody realises they are asking for a change rather
  // than reporting a fault, and landing them on the wrong tab would make that
  // offer worse than not making it.
  const [searchParams] = useSearchParams();
  const kindParam = searchParams.get("kind");
  // `?offering=<id>` (19 Oct, the email intake's form-link reply) opens that
  // offering's form on the catalog tab.
  const offeringParam = searchParams.get("offering");
  const [kind, setKind] = useState<TicketKind>(kindFromParam(kindParam, offeringParam));
  // Those links point at this same page, so React Router keeps it mounted:
  // follow a changed `?kind=` to the matching tab rather than only reading it
  // on mount (adjusting state during render, not in an effect).
  const [seenKindParam, setSeenKindParam] = useState(kindParam);
  if (kindParam !== seenKindParam) {
    setSeenKindParam(kindParam);
    setKind(kindFromParam(kindParam, offeringParam));
  }

  // Opened from a new starter case (`?starter=<case>&step=<step>`, 2 Oct):
  // the offering and its first answers come from the case.
  // Only while the flow is on for this person: `has_starter_cases` is false
  // whenever STARTER_FLOW_ENABLED is off, and then `?starter=` is ignored.
  // WHY (5 Oct): `has_starter_cases` is also false while a cold case list
  // outlasts its timeout (right after a deploy), and the IT Setup link opened
  // a blank catalog form on UAT. With the access front door on, the link is
  // honoured too: the prefill endpoint checks who may see the case itself.
  const { data: me } = useMe();
  const starterTarget =
    me?.has_starter_cases === true || me?.access_front_door === true
      ? starterStepFromParams(searchParams.get("starter"), searchParams.get("step"))
      : null;

  // "Report a problem" on a finished ticket (`?follow_up=<ticket id>`, 9 Oct):
  // the incident names that ticket and goes to its owner. Read as the
  // signed-in person, so only their own ticket is ever followed.
  const followUpId = searchParams.get("follow_up");
  const followedQuery = useTicket(followUpId ?? undefined);
  const followed = followUpId !== null ? (followedQuery.data ?? null) : null;

  const breadcrumbs = (
    <AppBreadcrumbs items={[{ label: t("title"), to: "/tickets" }, { label: t("new.title") }]} />
  );

  return (
    <PageContainer title={t("new.title")} subtitle={t("new.subtitle")} breadcrumbs={breadcrumbs}>
      <Tabs.Root value={kind} onValueChange={(value) => setKind(value as TicketKind)}>
        <Tabs.List aria-label={t("create.pathLabel")}>
          <Tabs.Tab value="INCIDENT">
            <span className="flex items-center gap-2">
              <Icon icon={CircleAlert} size="sm" />
              {t("kind.INCIDENT")}
            </span>
          </Tabs.Tab>
          <Tabs.Tab value="SERVICE_REQUEST">
            <span className="flex items-center gap-2">
              <Icon icon={ShoppingBag} size="sm" />
              {t("kind.SERVICE_REQUEST")}
            </span>
          </Tabs.Tab>
          <Tabs.Indicator />
        </Tabs.List>

        <Tabs.Panel value="INCIDENT">
          {followed !== null ? (
            <Alert
              tone="info"
              className="mt-6"
              title={t("reportProblem.followUpTitle", { number: followed.ticket_number })}
            >
              {t("reportProblem.followUpBody", { subject: followed.subject.text })}
            </Alert>
          ) : null}
          <SectionCard description={t("create.incident.intro")} className="mt-6">
            {/* Remounted once the followed ticket arrives, so its subject prefills. */}
            <IncidentForm
              key={followed?.ticket_id ?? "new"}
              followUp={
                followed === null
                  ? null
                  : {
                      ticketId: followed.ticket_id,
                      subject: followed.subject.original_text ?? followed.subject.text,
                    }
              }
            />
          </SectionCard>
        </Tabs.Panel>

        <Tabs.Panel value="SERVICE_REQUEST">
          <div className="mt-6 flex flex-col gap-4">
            {starterTarget === null ? (
              <>
                <Typography variant="body-sm" tone="muted">
                  {t("create.request.intro")}
                </Typography>
                <ServiceRequestForm key={offeringParam ?? ""} initialOfferingId={offeringParam} />
              </>
            ) : (
              <StarterRequestForm target={starterTarget} />
            )}
          </div>
        </Tabs.Panel>
      </Tabs.Root>
    </PageContainer>
  );
}

/**
 * The request form for one step of a new starter case, prefilled from it.
 *
 * If the case cannot fill it in (the offering is not in this person's
 * catalog, the account is not confirmed yet), the reason is shown with a way
 * back to the case — never an empty form that looks prefilled.
 *
 * Filed, it returns to the case, not the new ticket (5 Oct, product owner's
 * UAT): to the access request while the front door is on (`/starters` lands
 * there anyway), else to the New starter page. Both refresh first.
 */
function StarterRequestForm({ target }: { target: { caseId: string; step: StarterStep } }) {
  const { t } = useT("tickets");
  const prefill = useStarterPrefill(target);
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const returnTo = {
    to:
      me?.access_front_door === true
        ? accessCasePath(target.caseId)
        : `/starters/${encodeURIComponent(target.caseId)}`,
    onCreated: () => {
      void queryClient.invalidateQueries({ queryKey: starterKeys.all });
      void queryClient.invalidateQueries({ queryKey: accessKeys.all });
    },
  };
  const back = (
    <Button variant="outline" tone="neutral" render={<Link to={returnTo.to} />}>
      {t("starters.form.backToCase")}
    </Button>
  );
  if (prefill.isPending) {
    return <LoadingState label={t("starters.form.loading")} />;
  }
  if (prefill.isError) {
    return <ErrorState error={prefill.error} actions={back} />;
  }
  return (
    <>
      <Alert tone="info">{t("starters.form.banner")}</Alert>
      <ServiceRequestForm
        key={`${target.caseId}:${target.step}`}
        starterPrefill={{
          offeringId: prefill.data.offering_id,
          subject: prefill.data.subject,
          values: prefill.data.values ?? [],
        }}
        returnTo={returnTo}
      />
    </>
  );
}
